import React from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import usePageMeta from 'hooks/usePageMeta';
import { cx } from 'pages/Homepage/hooks';
import {
  computeBookingPricing,
  T2B_FEE_CAP,
  T2B_FEE_FLOOR,
  VAT_RATE,
} from 'utils/bookingPricing';
import { EASYPAY_ONBOARDING_URL } from 'utils/easypayPayout';

// The worked example runs through the same arithmetic the checkout uses, so
// the figures here can never drift from what a brand is actually charged.
const EXAMPLE_PRICE = 100;

const Card = ({ className, children }) => (
  <div className={cx('rounded-2xl border border-line bg-white', className)}>
    {children}
  </div>
);

const SplitTile = ({ label, amount, detail, className }) => (
  <div className={cx('flex-1 rounded-xl px-5 py-4', className)}>
    <p className="text-[13px] opacity-70">{label}</p>
    <p className="mt-1 font-display text-[28px] font-extrabold leading-tight">
      {amount}
    </p>
    <p className="mt-1 text-[12.5px] opacity-70">{detail}</p>
  </div>
);

const SplitOperator = ({ children }) => (
  <span
    aria-hidden="true"
    className="hidden self-center text-lg text-line-strong md:block"
  >
    {children}
  </span>
);

const Payments = () => {
  const { t, i18n } = useTranslation();

  usePageMeta({
    title: t('payments_page.title'),
    description: t('payments_page.lead'),
  });

  const locale = i18n.language?.startsWith('pt') ? 'pt-PT' : 'en-GB';
  const money = (value) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(value);
  const net = (value) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);

  const example = computeBookingPricing(EXAMPLE_PRICE);
  const promoterGross = example.total - example.t2bFeeGross;
  const vatPercent = Math.round(VAT_RATE * 100);

  const facts = [
    { value: '5%', body: t('payments_page.fact_fee') },
    { value: 'Easypay', body: t('payments_page.fact_split') },
    { value: t('payments_page.fact_payout_value'), body: t('payments_page.fact_payout') },
  ];

  const steps = [1, 2, 3, 4].map((n) => ({
    n,
    title: t(`payments_page.step_${n}_title`),
    body: t(`payments_page.step_${n}_body`),
  }));

  const onboarding = [
    { key: 'account', highlight: true },
    { key: 'company', highlight: true },
    { key: 'kyc', highlight: false },
    { key: 'linked', highlight: false },
  ];

  return (
    <div className="bg-paper">
      {/* ============ HERO ============ */}
      <section className="border-b border-line bg-white">
        <div className="mx-auto max-w-[1080px] px-6 py-16 sm:py-20">
          <h1 className="font-display text-[44px] font-extrabold leading-[1.05] tracking-[-0.02em] text-ink sm:text-[56px]">
            {t('payments_page.title')}
          </h1>
          <p className="mt-5 max-w-[640px] text-[17px] leading-[1.7] text-muted-2 sm:text-[18px]">
            {t('payments_page.lead')}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1080px] space-y-10 px-6 py-14 sm:py-16">
        {/* ============ KEY FACTS ============ */}
        <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {facts.map((fact) => (
            <Card key={fact.value} className="p-6">
              <p className="font-display text-[30px] font-extrabold leading-tight text-ink">
                {fact.value}
              </p>
              <p className="mt-3 text-[15px] leading-[1.65] text-muted-2">
                {fact.body}
              </p>
            </Card>
          ))}
        </section>

        {/* ============ WORKED EXAMPLE ============ */}
        <Card className="p-6 sm:p-8">
          <h2 className="font-display text-[24px] font-extrabold text-ink">
            {t('payments_page.example_title')}
          </h2>
          <p className="mt-2 text-[15px] text-muted-2">
            {t('payments_page.example_intro', {
              price: net(example.price),
              fee: net(example.t2bFee),
            })}
          </p>

          <div className="mt-6 flex flex-col gap-3 md:flex-row md:gap-4">
            <SplitTile
              className="bg-paper-2 text-ink"
              label={t('payments_page.example_brand_pays')}
              amount={money(example.total)}
              detail={t('payments_page.plus_vat', {
                amount: net(example.price + example.t2bFee),
              })}
            />
            <SplitOperator>=</SplitOperator>
            <SplitTile
              className="bg-brand text-ink"
              label={t('payments_page.example_to_promoter')}
              amount={money(promoterGross)}
              detail={t('payments_page.plus_vat', { amount: net(example.price) })}
            />
            <SplitOperator>+</SplitOperator>
            <SplitTile
              className="bg-paper-bg text-ink"
              label={t('payments_page.example_to_t2b')}
              amount={money(example.t2bFeeGross)}
              detail={t('payments_page.plus_vat', { amount: net(example.t2bFee) })}
            />
          </div>

          <p className="mt-5 text-[13px] leading-[1.6] text-muted">
            {t('payments_page.example_note', {
              vat: vatPercent,
              floor: net(T2B_FEE_FLOOR),
              cap: net(T2B_FEE_CAP),
            })}
          </p>
        </Card>

        {/* ============ FROM EVENT TO PAYOUT ============ */}
        <section>
          <h2 className="mb-5 font-display text-[24px] font-extrabold text-ink">
            {t('payments_page.steps_title')}
          </h2>
          <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step) => (
              <li key={step.n}>
                <Card className="h-full p-5">
                  <span
                    className={cx(
                      'grid h-7 w-7 place-items-center rounded-full text-[13px] font-bold text-ink',
                      step.n <= 2 ? 'bg-brand' : 'bg-paper-2'
                    )}
                  >
                    {step.n}
                  </span>
                  <h3 className="mt-4 text-[16px] font-bold leading-snug text-ink">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-[14.5px] leading-[1.65] text-muted-2">
                    {step.body}
                  </p>
                </Card>
              </li>
            ))}
          </ol>
        </section>

        {/* ============ EASYPAY ONBOARDING ============ */}
        <Card className="p-6 sm:p-8">
          <h2 className="font-display text-[24px] font-extrabold text-ink">
            {t('payments_page.easypay_title')}
          </h2>
          <p className="mt-2 max-w-[720px] text-[15px] leading-[1.7] text-muted-2">
            {t('payments_page.easypay_intro')}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {onboarding.map(({ key, highlight }) => (
              <div key={key} className="rounded-xl bg-paper-2 px-4 py-4">
                <p
                  className={cx(
                    'text-[11.5px] font-bold uppercase tracking-[0.06em]',
                    highlight ? 'text-yellow-04' : 'text-muted'
                  )}
                >
                  {t(`payments_page.easypay_${key}_eyebrow`)}
                </p>
                <p className="mt-1 text-[15px] font-bold text-ink">
                  {t(`payments_page.easypay_${key}_title`)}
                </p>
                <p className="mt-1 text-[13.5px] leading-[1.55] text-muted-2">
                  {t(`payments_page.easypay_${key}_body`)}
                </p>
              </div>
            ))}
          </div>

          <a
            href={EASYPAY_ONBOARDING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center rounded-lg bg-brand px-6 py-3 text-[15px] font-semibold text-ink transition-colors hover:bg-brand-hover"
          >
            {t('payments_page.easypay_cta')}
          </a>
        </Card>

        <p className="text-[14px] leading-[1.6] text-muted-2">
          <Trans
            i18nKey="payments_page.footnote"
            components={{
              terms: (
                <Link
                  to="/terms-conditions"
                  className="text-ink underline underline-offset-2"
                />
              ),
              contact: (
                <Link
                  to="/contact-us"
                  className="text-ink underline underline-offset-2"
                />
              ),
            }}
          />
        </p>
      </div>
    </div>
  );
};

export default Payments;
